import * as React from 'react'

import { Alert, Box, Link, Section, Text } from '@factorialco/deprecated-design-system'

import { observer } from 'mobx-react'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import i18n from 'modules/core/lib/i18n'

import DimensionsAlertModal from 'modules/finance/components/Dimensions/DimensionsAlertModal'
import EmployeeDimensionsList from 'modules/finance/components/Dimensions/EmployeeDimensionsList'
import SetAssignmentsPanel from 'modules/finance/components/Dimensions/SetAssignmentsPanel'
import useDimensionAssignments from 'modules/finance/components/Dimensions/SetAssignmentsPanel/useDimensionAssignments'
import Loading from 'modules/finance/components/Loading'
import useEmployeeDimensionsAssignments, {
  type SingleValueDimensionAssignment,
} from 'modules/finance/hooks/useEmployeeDimensionsAssignments'
import useTrackEmployeeChanges from 'modules/finance/hooks/useTrackEmployeeChanges'

interface Props {
  employeeId: number
  employeeFullName: string
  legalEntityId: number
  isCurrentEmployee: boolean
  managerId: number | null
  locationId: number | null
  teamIdsSignature?: string
}

const EmployeeDimensions = ({
  employeeId,
  employeeFullName,
  legalEntityId,
  isCurrentEmployee,
  managerId,
  locationId,
  teamIdsSignature = '',
}: Props) => {
  const {
    isLoading,
    dimensionsWithAssignments,
    canManageEmployeeDimensions,
    canSeeEmployeeDimensions,
    thereAreDimensions,
    refetch,
  } = useEmployeeDimensionsAssignments({ employeeId, legalEntityId })

  const { saveDimensionAssignment, endDimensionAssignment } = useDimensionAssignments()

  const { employee: currentEmployee } = useCurrentSession()

  const hasAssignments = dimensionsWithAssignments.some(
    (d) =>
      (d.type == 'single-value' && d.assignmentValue) ||
      (d.type == 'multiple-value' && d.assignments.length > 0)
  )

  const { navigation } = useNavigation()
  const [manageDimensionsSidepanelVisible, setManageDimensionsSidepanelVisible] =
    React.useState(false)
  const [dimensionsChangedAlertModalVisible, setDimensionsChangedAlertModalVisible] =
    React.useState(false)
  const [alertDescription, setAlertDescription] = React.useState('')

  const showAlert = (description: string) => {
    setAlertDescription(description)
    setDimensionsChangedAlertModalVisible(true)
  }

  const onClickDimensionsModal = () => {
    setManageDimensionsSidepanelVisible(true)
  }

  const onCloseManageDimensionsSidepanel = () => {
    setManageDimensionsSidepanelVisible(false)
  }

  const onCloseDimensionsAlertModal = () => {
    setDimensionsChangedAlertModalVisible(false)
  }

  const handleOnSubmitAlertModal = () => {
    setDimensionsChangedAlertModalVisible(false)
    setManageDimensionsSidepanelVisible(true)
  }

  useTrackEmployeeChanges({
    employeeId,
    managerId,
    locationId,
    teamIdsSignature,
    hasAssignments: hasAssignments && thereAreDimensions,
    onChange: showAlert,
  })

  return (
    <Loading loading={isLoading}>
      {canSeeEmployeeDimensions && (
        <>
          <Section
            illustration="report-library"
            title={i18n.t('finance.employee_dimensions_info.title')}
            description={i18n.tCurrent('finance.employee_dimensions_info', isCurrentEmployee)}
            link={{
              href: navigation.settings.finance.fullPath(),
              label: i18n.t('finance.employee_dimensions_info.manage_dimensions'),
            }}
            action={
              canManageEmployeeDimensions && thereAreDimensions
                ? {
                    label: i18n.t('finance.employee_dimensions_info.edit_assignments'),
                    onClick: onClickDimensionsModal,
                  }
                : undefined
            }
          >
            <Box paddingLeft="s64">
              {thereAreDimensions ? (
                <EmployeeDimensionsList dimensions={dimensionsWithAssignments} />
              ) : (
                <Alert color="warning">
                  <Box flexDirection="column" gap="s16">
                    <Text.Normal size="300" weight="medium">
                      {i18n.t('finance.employee_dimensions_info.tip.title')}
                    </Text.Normal>
                    {canManageEmployeeDimensions && (
                      <Link.Router to={navigation.settings.finance.fullPath()}>
                        {i18n.t('finance.employee_dimensions_info.tip.link')}
                      </Link.Router>
                    )}
                  </Box>
                </Alert>
              )}
            </Box>
          </Section>
          <SetAssignmentsPanel
            employeeId={employeeId}
            employeeFullName={employeeFullName}
            legalEntityId={legalEntityId}
            open={manageDimensionsSidepanelVisible}
            dimensionsAssignments={dimensionsWithAssignments}
            saveAssignment={(id: number, dim: SingleValueDimensionAssignment) =>
              saveDimensionAssignment(id, dim)
            }
            endAssignment={endDimensionAssignment}
            onClose={onCloseManageDimensionsSidepanel}
            refetch={refetch}
          />
          {dimensionsChangedAlertModalVisible && (
            <DimensionsAlertModal
              description={alertDescription}
              onSubmit={handleOnSubmitAlertModal}
              onCloseDimensionsAlertModal={onCloseDimensionsAlertModal}
            />
          )}
        </>
      )}
    </Loading>
  )
}

export default observer(EmployeeDimensions)
